import { postAuthJson, readTokenResponse, SESSION_REFRESH_PATH } from "./auth-http";
import {
  clearAccessToken,
  clearLogoutPending,
  getAccessToken,
  isRefreshDue,
  monotonicNow,
  msUntilRefresh,
  saveAccessToken,
} from "./session-store";

import type { TokenResponse } from "./session-store";

export type RefreshOutcome = "refreshed" | "signed_out" | "unavailable" | "failed";

type AttemptOutcome = RefreshOutcome | "in_progress";

// A 409 retry resends the cookie another tab may have just rotated; the backend revokes the
// session if that happens after its reuse grace, which can be as short as 1 second.
const IN_PROGRESS_RETRY_DELAYS_MS = [100, 150, 250];
const IN_PROGRESS_RETRY_WINDOW_MS = 600;
const IN_PROGRESS_COOLDOWN_MS = 10_000;
const UNAVAILABLE_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];
const UNAVAILABLE_RESCHEDULE_MS = 30_000;

let sessionGeneration = 0;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;
let refreshInFlight: Promise<RefreshOutcome> | null = null;
let inProgressCooldownUntil: number | null = null;

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function withJitter(ms: number): number {
  return ms + Math.random() * ms * 0.2;
}

function cancelRefreshTimer() {
  if (refreshTimer !== undefined) {
    clearTimeout(refreshTimer);
    refreshTimer = undefined;
  }
}

function scheduleRefresh(delayMs: number) {
  cancelRefreshTimer();
  refreshTimer = setTimeout(() => {
    refreshTimer = undefined;
    void refreshSession();
  }, delayMs);
}

function handleVisibilityChange() {
  if (document.visibilityState === "visible" && isRefreshDue()) {
    void refreshSession();
  }
}

function applyTokens(tokens: TokenResponse) {
  saveAccessToken(tokens);
  scheduleRefresh(msUntilRefresh() ?? 0);
  document.addEventListener("visibilitychange", handleVisibilityChange);
}

export function startSession(tokens: TokenResponse) {
  sessionGeneration += 1;
  refreshInFlight = null;
  inProgressCooldownUntil = null;
  clearLogoutPending();
  applyTokens(tokens);
}

export function stopSession() {
  sessionGeneration += 1;
  refreshInFlight = null;
  inProgressCooldownUntil = null;
  cancelRefreshTimer();
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  clearAccessToken();
}

async function attemptRefresh(generation: number): Promise<AttemptOutcome> {
  let response: Response;
  try {
    response = await postAuthJson(SESSION_REFRESH_PATH);
  } catch {
    return "unavailable";
  }

  if (generation !== sessionGeneration) {
    return "failed";
  }

  if (response.ok) {
    const tokens = await readTokenResponse(response);
    if (tokens === null || generation !== sessionGeneration) {
      return "failed";
    }
    applyTokens(tokens);
    return "refreshed";
  }

  switch (response.status) {
    case 401: {
      stopSession();
      return "signed_out";
    }
    case 409: {
      return "in_progress";
    }
    case 503: {
      return "unavailable";
    }
    default: {
      return "failed";
    }
  }
}

async function runRefresh(): Promise<RefreshOutcome> {
  const generation = sessionGeneration;
  let firstInProgressSentAt: number | null = null;
  let inProgressRetries = 0;
  let unavailableRetries = 0;

  for (;;) {
    const sentAt = monotonicNow();
    const outcome = await attemptRefresh(generation);

    if (outcome === "in_progress") {
      firstInProgressSentAt ??= sentAt;
      const delayMs = IN_PROGRESS_RETRY_DELAYS_MS[inProgressRetries];
      inProgressRetries += 1;
      const retryAt = monotonicNow() + (delayMs ?? 0);
      if (delayMs === undefined || retryAt - firstInProgressSentAt > IN_PROGRESS_RETRY_WINDOW_MS) {
        inProgressCooldownUntil = monotonicNow() + IN_PROGRESS_COOLDOWN_MS;
        return "unavailable";
      }
      await wait(delayMs);
    } else if (outcome === "unavailable") {
      const baseDelay = UNAVAILABLE_RETRY_DELAYS_MS[unavailableRetries];
      unavailableRetries += 1;
      if (baseDelay === undefined) {
        if (getAccessToken() !== null && generation === sessionGeneration) {
          scheduleRefresh(UNAVAILABLE_RESCHEDULE_MS);
        }
        return "unavailable";
      }
      await wait(withJitter(baseDelay));
    } else {
      return outcome;
    }

    if (generation !== sessionGeneration) {
      return "failed";
    }
  }
}

export function refreshSession(): Promise<RefreshOutcome> {
  if (refreshInFlight !== null) {
    return refreshInFlight;
  }
  if (inProgressCooldownUntil !== null && monotonicNow() < inProgressCooldownUntil) {
    return Promise.resolve("unavailable");
  }
  const refresh = runRefresh().finally(() => {
    if (refreshInFlight === refresh) {
      refreshInFlight = null;
    }
  });
  refreshInFlight = refresh;
  return refresh;
}
